import { TimeInForce } from '@katanaperps/katana-perps-sdk';
import React from 'react';
import styled from 'styled-components';

import MaterialSymbol from 'components/basic/icons/MaterialSymbol';
import Dropdown from 'components/basic/inputs/Dropdown';
import ToggleSlider from 'components/basic/inputs/ToggleSlider/ToggleSlider';
import Tooltip from 'components/basic/tooltip/Tooltip';

import { InputPairPanel, InputWrap, InputTitle } from './layout';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px;
  .isMobileStyle & {
    grid-template-columns: 1fr 85px;
  }
`;

const ReduceOnlyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const WarningIcon = styled(MaterialSymbol)`
  color: var(--red3);
  cursor: help;
`;

type Props = {
  isDisabled: boolean;

  isLimitOrder: boolean;
  hasStop: boolean;

  reduceOnly: boolean;
  onChangeReduceOnly: (value: boolean) => void;
  /** undefined = loading / wallet not connected; false = confirmed no open position */
  hasOpenPosition: boolean | undefined;

  postOnly: boolean;
  onChangePostOnly: (value: boolean) => void;

  timeInForce: TimeInForce;
  onChangeTimeInForce: (value: TimeInForce) => void;

  conditionalTpSl: boolean;
  onChangeConditionalTpSlModal: (check: boolean) => void;
};

// TODO: BuySell: apply same for placing order - that we ignore those fields eg for market order, ...
const BuyFieldsAdvanced = ({
  isDisabled,
  isLimitOrder,
  hasStop,
  reduceOnly,
  onChangeReduceOnly,
  hasOpenPosition,
  postOnly,
  onChangePostOnly,
  timeInForce,
  onChangeTimeInForce,
  conditionalTpSl,
  onChangeConditionalTpSlModal,
}: Props) => {
  const reduceOnlyWithoutPosition =
    reduceOnly && !isDisabled && hasOpenPosition === false;
  return (
    <Grid>
      <div>
        <InputPairPanel $noTopPadding $noBottomPadding>
          <InputTitle />
          <InputWrap>
            <ReduceOnlyRow>
              <ToggleSlider
                checked={reduceOnly}
                onChange={onChangeReduceOnly}
                disabled={isDisabled}
              >
                Reduce-Only
              </ToggleSlider>
              {reduceOnlyWithoutPosition && (
                <Tooltip
                  wrapper="inlineDiv"
                  maxWidthPx={200}
                  text="No open position — orders will be rejected while Reduce-Only is on."
                  type="warning"
                  pointer
                >
                  <WarningIcon icon="warning" filled rounded fontSize="small" />
                </Tooltip>
              )}
            </ReduceOnlyRow>
            <div style={{ padding: 2 }} />
          </InputWrap>
        </InputPairPanel>

        {(isLimitOrder || hasStop) && (
          <InputPairPanel $noTopPadding>
            <InputTitle />
            <InputWrap>
              <ToggleSlider
                checked={postOnly}
                onChange={onChangePostOnly}
                disabled={isDisabled}
              >
                Post-Only
              </ToggleSlider>
            </InputWrap>
          </InputPairPanel>
        )}

        <InputPairPanel $noTopPadding $noBottomPadding>
          <InputTitle />
          <InputWrap>
            <ToggleSlider
              checked={conditionalTpSl}
              onChange={onChangeConditionalTpSlModal}
              disabled={isDisabled}
            >
              TP/SL
            </ToggleSlider>
            <div style={{ height: 8 }} />
          </InputWrap>
        </InputPairPanel>
      </div>
      {(isLimitOrder || hasStop) && (
        <div>
          <Dropdown<TimeInForce>
            disabled={isDisabled || postOnly}
            slim
            options={[
              {
                label: 'Good Until Canceled',
                labelSelected: 'GTC',
                value: TimeInForce.gtc,
              },
              {
                label: 'Immediate or Cancel',
                labelSelected: 'IoC',
                value: TimeInForce.ioc,
              },
              {
                label: 'Fill or Kill',
                labelSelected: 'FoK',
                value: TimeInForce.fok,
              },
            ]}
            onChange={onChangeTimeInForce}
            selected={timeInForce}
            noBorder
            alignTextRight
          />
        </div>
      )}
    </Grid>
  );
};

export default BuyFieldsAdvanced;
